import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuArrowRight,
  LuCheck,
  LuCircleEllipsis,
  LuCloudUpload,
  LuHeadphones,
  LuLock,
  LuMail,
  LuMonitor,
  LuSend,
  LuShieldCheck,
  LuShoppingBag,
  LuShoppingCart,
  LuStar,
  LuTag,
  LuUsers,
} from "react-icons/lu";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import PhoneCountryCodeSelect from "../../components/PhoneCountryCodeSelect";
import StorageService from "../../services/StorageService";
import {
  createPlatformFeedback,
  feedbackExtractMessage,
} from "../../services/platformFeedback.service";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/feedback.scss";

const FEEDBACK_AREAS = [
  { id: "platform", label: "Website / Platform", icon: LuMonitor, tone: "blue" },
  { id: "products", label: "Products & Services", icon: LuShoppingBag, tone: "green" },
  { id: "buying", label: "Buying Experience", icon: LuShoppingCart, tone: "orange" },
  { id: "selling", label: "Selling Experience", icon: LuTag, tone: "purple" },
  { id: "support", label: "Support Experience", icon: LuHeadphones, tone: "cyan" },
  { id: "other", label: "Other", icon: LuCircleEllipsis, tone: "gray" },
];

const REASONS = [
  {
    title: "Helps Us Improve",
    description: "Your feedback helps us enhance our platform and features.",
    icon: LuCheck,
    tone: "green",
  },
  {
    title: "Better Experience",
    description: "We use your input to provide a better experience for all users.",
    icon: LuUsers,
    tone: "orange",
  },
  {
    title: "We Value You",
    description: "Your feedback is important to us and always appreciated.",
    icon: LuShieldCheck,
    tone: "purple",
  },
];

function getInitialForm() {
  const user = StorageService.getUser() || {};
  return {
    area: "platform",
    feedbackType: "Suggestion",
    subject: "",
    feedback: "",
    rating: 0,
    name:
      user.name ||
      user.fullName ||
      user.full_name ||
      [user.firstName || user.first_name, user.lastName || user.last_name].filter(Boolean).join(" ") ||
      "",
    email: user.email || user.email_address || "",
    countryCode: "+91",
    phone: String(user.phone || user.mobile || user.mobile_number || "").replace(/^\+?\d{1,3}\s*/, ""),
  };
}

export default function FeedbackPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(getInitialForm);
  const [submitting, setSubmitting] = useState(false);
  const [attachment, setAttachment] = useState(null);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!form.subject.trim()) {
      toast.error("Please enter a subject.");
      return;
    }
    if (form.feedback.trim().length < 10) {
      toast.error("Feedback must be at least 10 characters.");
      return;
    }
    if (!form.rating) {
      toast.error("Please select an overall rating.");
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (form.phone) {
      const digits = form.phone.replace(/\D/g, "");
      if (digits.length < 8 || digits.length > 15) {
        toast.error("Please enter a valid phone number.");
        return;
      }
    }

    const payload = new FormData();
    payload.append("areaCode", form.area);
    payload.append("feedbackType", form.feedbackType);
    payload.append("subject", form.subject.trim());
    payload.append("description", form.feedback.trim());
    payload.append("rating", String(form.rating));
    payload.append("customerName", form.name.trim());
    payload.append("customerEmail", form.email.trim());
    payload.append("customerCountryCode", form.countryCode || "+91");
    payload.append("customerPhone", form.phone.trim());
    if (attachment) payload.append("attachment", attachment);

    setSubmitting(true);
    try {
      const data = await createPlatformFeedback(payload);
      const success = data?.successPage || data;
      navigate("/help-center/feedback-submitted", {
        state: {
          feedbackNumber: success.feedbackNumber || data?.feedback?.feedbackNumber,
          subject: success.subject || form.subject.trim(),
          status: success.status || "New",
          createdAt: success.createdAt || new Date().toISOString(),
          rating: success.rating || form.rating,
          feedbackType: success.feedbackType || form.feedbackType,
          feedbackId: data?.feedback?.id,
        },
      });
    } catch (error) {
      toast.error(feedbackExtractMessage(error, "Could not submit feedback. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback-page">
      <B2BHubHomeHeader />

      <main className="feedback-main">
        <nav className="feedback-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>&gt;</span>
          <Link to="/help-center">Help Center</Link>
          <span aria-hidden>&gt;</span>
          <span aria-current="page">Feedback</span>
        </nav>

        <header className="feedback-heading">
          <h1>Feedback</h1>
          <p>
            We value your feedback! Share your thoughts, suggestions or experience to help us
            improve Trocanet for you and other businesses.
          </p>
        </header>

        <div className="feedback-layout">
          <form className="feedback-form" onSubmit={onSubmit}>
            <fieldset className="feedback-fieldset">
              <legend>
                1. What is your feedback about? <em>*</em>
              </legend>
              <div className="feedback-areas">
                {FEEDBACK_AREAS.map((item) => {
                  const Icon = item.icon;
                  const selected = form.area === item.id;
                  return (
                    <label key={item.id} className={`feedback-area${selected ? " is-selected" : ""}`}>
                      <input
                        type="radio"
                        name="area"
                        value={item.id}
                        checked={selected}
                        onChange={onChange}
                      />
                      <span className={`feedback-area__icon feedback-tone--${item.tone}`} aria-hidden>
                        <Icon />
                      </span>
                      <strong>{item.label}</strong>
                      <span className="feedback-area__radio" aria-hidden />
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="feedback-field-row">
              <label className="feedback-field">
                <span>
                  2. Feedback Type <em>*</em>
                </span>
                <select className="input-pill" name="feedbackType" value={form.feedbackType} onChange={onChange}>
                  <option>Suggestion</option>
                  <option>Compliment</option>
                  <option>Complaint</option>
                  <option>Feature Request</option>
                  <option>Bug Report</option>
                </select>
              </label>

              <label className="feedback-field">
                <span>
                  3. Subject <em>*</em>
                </span>
                <input
                  className="input-pill"
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  maxLength={100}
                  placeholder="Enter a short subject"
                  required
                />
                <small>{form.subject.length} / 100</small>
              </label>
            </div>

            <label className="feedback-field">
              <span>
                4. Your Feedback <em>*</em>
              </span>
              <span className="feedback-textarea">
                <textarea
                  className="input-area"
                  name="feedback"
                  value={form.feedback}
                  onChange={onChange}
                  maxLength={2000}
                  placeholder="Please share your feedback in detail..."
                  required
                />
                <small>{form.feedback.length} / 2000</small>
              </span>
            </label>

            <fieldset className="feedback-fieldset feedback-rating">
              <legend>
                5. How would you rate your overall experience with Trocanet? <em>*</em>
              </legend>
              <div className="feedback-rating__row">
                <div className="feedback-stars" aria-label={`${form.rating} out of 5 stars`}>
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={value <= form.rating ? "is-active" : ""}
                      onClick={() => setForm((current) => ({ ...current, rating: value }))}
                      aria-label={`Rate ${value} out of 5`}
                    >
                      <LuStar aria-hidden />
                    </button>
                  ))}
                </div>
                <span>{form.rating ? `${form.rating} out of 5` : "Select a rating"}</span>
              </div>
            </fieldset>

            <label className="feedback-field">
              <span>Attachment (Optional)</span>
              <div className={`feedback-upload${attachment ? " has-file" : ""}`}>
                <LuCloudUpload aria-hidden />
                <div>
                  <strong>{attachment ? attachment.name : "Upload supporting file"}</strong>
                  <small>PDF, DOC, XLS, JPG, PNG · max 10MB</small>
                </div>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                />
              </div>
            </label>

            <section className="feedback-contact">
              <h2>6. Your Contact Information (Optional)</h2>
              <div className="feedback-contact__grid">
                <label className="feedback-field">
                  <span>Your Name</span>
                  <input
                    className="input-pill"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Enter your name"
                  />
                </label>
                <label className="feedback-field">
                  <span>Email Address</span>
                  <span className="feedback-input-icon">
                    <input
                      className="input-pill"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder="Enter your email"
                    />
                    <LuMail aria-hidden />
                  </span>
                </label>
                <label className="feedback-field feedback-field--phone">
                  <span>Phone Number</span>
                  <span className="feedback-phone">
                    <PhoneCountryCodeSelect
                      name="countryCode"
                      value={form.countryCode}
                      onChange={onChange}
                      aria-label="Country code"
                    />
                    <input
                      className="input-pill"
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      placeholder="Enter phone number"
                    />
                  </span>
                </label>
              </div>
            </section>

            <div className="feedback-privacy">
              <LuLock aria-hidden />
              <span>Your information will be used only to follow up on your feedback, if needed.</span>
            </div>

            <div className="feedback-actions">
              <button type="button" className="feedback-btn feedback-btn--cancel" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button
                type="submit"
                className="feedback-btn feedback-btn--submit"
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit Feedback"}
                {!submitting ? <LuSend aria-hidden /> : null}
              </button>
            </div>
          </form>

          <aside className="feedback-aside">
            <section className="feedback-side-card">
              <h2>Why your feedback matters?</h2>
              <div className="feedback-reasons">
                {REASONS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div className="feedback-reason" key={item.title}>
                      <span className={`feedback-reason__icon feedback-tone--${item.tone}`} aria-hidden>
                        <Icon />
                      </span>
                      <div>
                        <strong>{item.title}</strong>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="feedback-side-card feedback-before">
              <h2>Before you submit</h2>
              <ul>
                <li>
                  <LuCheck aria-hidden />
                  <span>Search your question in Help Center – you might find the answer.</span>
                </li>
                <li>
                  <LuCheck aria-hidden />
                  <span>For support related issues, please raise a support ticket.</span>
                </li>
                <li>
                  <LuCheck aria-hidden />
                  <span>Do not share sensitive or personal information.</span>
                </li>
              </ul>
            </section>

            <section className="feedback-help-card">
              <h2>Need Immediate Help?</h2>
              <p>If you need immediate assistance, our support team is here for you.</p>
              <Link to="/contact?enquiry=general">
                Contact Support <LuArrowRight aria-hidden />
              </Link>
            </section>
          </aside>
        </div>
      </main>

      <HubHomeFooter />
    </div>
  );
}
